import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "@common";
import CurrencyBadge from "../CurrencyBadge";
import { COUNT_LABELS, PAYMENT_PLANS, deriveStatementDates } from "./statementPlan";

// ─── field primitives (mirroring StatementPaymentModal) ──────────────────────

const FIELD_CLS =
  "w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary placeholder:text-palette-gray-placeholder focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border transition-colors";

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-sm font-medium text-palette-gray-heading">{children}</label>
  );
}

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${FIELD_CLS} appearance-none pr-9`}
        >
          {placeholder != null && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-palette-gray-secondary"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

function DayField({ label, value, onChange, hint }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number"
        inputMode="numeric"
        min="1"
        max="31"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 25"
        className={FIELD_CLS}
      />
      {hint && <p className="mt-1 text-xs text-palette-gray-secondary">{hint}</p>}
    </div>
  );
}

// Which month `due_day` falls in. Explicit because a day pair cannot express it:
// "the 3rd → the 24th" is a valid 21-day cycle AND a valid 52-day one.
const DUE_MONTH_OPTIONS = [
  { value: "0", label: "Same month as the statement" },
  { value: "1", label: "The month after the statement" },
  { value: "2", label: "Two months after the statement" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "cancelled", label: "Cancelled" },
];

// `custom_split` is absent by design: a plan stores no balance, so a hand-chosen
// division has nothing to re-divide next month. The API rejects it too.
const PLANNABLE_PAYMENT_PLANS = PAYMENT_PLANS.filter((p) => p.value !== "custom_split");

function StatusChips({ value, onChange }) {
  return (
    <div>
      <Label>Status</Label>
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => {
          const selected = value === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onChange(s.value)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                selected
                  ? "border-palette-indigo-border bg-palette-indigo-soft text-palette-indigo-text"
                  : "border-palette-gray-divider bg-white text-palette-gray-secondary hover:bg-palette-gray-subtle"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  selected ? "bg-palette-indigo-badge" : "bg-palette-gray-placeholder"
                }`}
              />
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_FORM = {
  from_bank_account: "",
  to_card_account: "",
  statement_day: "",
  due_day: "",
  due_month_offset: "1",
  payment_plan: "full_on_due",
  plan_count: "",
  status: "active",
  notes: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * "Edit saved plan" modal.
 *
 * Its own form rather than a reuse of StatementPaymentModal, because a plan is
 * deliberately missing that modal's two central fields: it has no balance and no
 * calendar dates, only a day-of-month pair. Feeding a plan through a form that
 * requires both would mean inventing values just to satisfy the UI.
 *
 * The preview is the point of the day fields — "the 25th → the 20th" is abstract
 * until you see it become two real dates, and the next-month roll is invisible
 * otherwise.
 */
export default function StatementPlanModal({
  isOpen,
  onClose,
  onSave,
  bankAccounts = [],
  cardAccounts = [],
  initialValues = null,
  // Month the preview resolves against, "2026-09".
  month = null,
  saving = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isOpen) setForm({ ...EMPTY_FORM, ...(initialValues ?? {}) });
  }, [isOpen, initialValues]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const bankOptions = useMemo(
    () => bankAccounts.map((a) => ({ value: a.id, label: a.label })),
    [bankAccounts],
  );
  const cardOptions = useMemo(
    () => cardAccounts.map((a) => ({ value: a.id, label: a.label })),
    [cardAccounts],
  );

  const selectedCard = useMemo(
    () => cardAccounts.find((a) => String(a.id) === String(form.to_card_account)) ?? null,
    [cardAccounts, form.to_card_account],
  );

  const needsCount = COUNT_LABELS[form.payment_plan] != null;
  const statementDay = Number(form.statement_day);
  const dueDay = Number(form.due_day);
  const daysValid =
    Number.isInteger(statementDay) &&
    statementDay >= 1 &&
    statementDay <= 31 &&
    Number.isInteger(dueDay) &&
    dueDay >= 1 &&
    dueDay <= 31;

  // Same derivation the statement modal uses when it pre-fills, so what is
  // previewed here is exactly what next month will produce.
  const preview = useMemo(() => {
    if (!daysValid || !month) return null;
    return deriveStatementDates(
      {
        statement_day: statementDay,
        due_day: dueDay,
        due_month_offset: Number(form.due_month_offset),
      },
      month,
    );
  }, [daysValid, month, statementDay, dueDay, form.due_month_offset]);

  const canSave =
    !!form.from_bank_account &&
    !!form.to_card_account &&
    String(form.from_bank_account) !== String(form.to_card_account) &&
    daysValid &&
    (!needsCount || Number(form.plan_count) > 0);

  function handleSave() {
    onSave?.({
      from_bank_account: form.from_bank_account,
      to_card_account: form.to_card_account,
      statement_day: statementDay,
      due_day: dueDay,
      due_month_offset: Number(form.due_month_offset),
      payment_plan: form.payment_plan,
      plan_count: needsCount ? Number(form.plan_count) : null,
      status: form.status,
      notes: form.notes ?? "",
    });
  }

  const footer = (
    <>
      <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        disabled={saving || !canSave}
        className="font-semibold"
      >
        {saving ? "Saving…" : "Save plan"}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit saved plan"
      subtitle="Reused every month — no balance and no dates, just the cycle and the split rule."
      size="lg"
      footer={footer}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="From bank account"
            value={form.from_bank_account}
            onChange={set("from_bank_account")}
            options={bankOptions}
            placeholder="Select bank account"
          />
          <SelectField
            label="To card account"
            value={form.to_card_account}
            onChange={set("to_card_account")}
            options={cardOptions}
            placeholder="Select card account"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DayField
            label="Statement day"
            value={form.statement_day}
            onChange={set("statement_day")}
            hint="Day of the month the statement is issued (1–31)."
          />
          <DayField
            label="Due day"
            value={form.due_day}
            onChange={set("due_day")}
            hint="Day of the month the balance is due (1–31)."
          />
        </div>

        <SelectField
          label="Due date falls in"
          value={String(form.due_month_offset)}
          onChange={set("due_month_offset")}
          options={DUE_MONTH_OPTIONS}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Payment plan"
            value={form.payment_plan}
            onChange={set("payment_plan")}
            options={PLANNABLE_PAYMENT_PLANS}
          />
          {needsCount ? (
            <div>
              <Label>{COUNT_LABELS[form.payment_plan]}</Label>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                value={form.plan_count}
                onChange={(e) => set("plan_count")(e.target.value)}
                placeholder="e.g. 3"
                className={FIELD_CLS}
              />
            </div>
          ) : (
            <StatusChips value={form.status} onChange={set("status")} />
          )}
        </div>

        {/* Status gets its own row when the count field took the slot above. */}
        {needsCount && <StatusChips value={form.status} onChange={set("status")} />}

        <div>
          <Label>Notes</Label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            placeholder="Carried onto each statement this plan creates…"
            className={`${FIELD_CLS} resize-none`}
          />
        </div>

        {/* What the two day numbers actually produce. */}
        <div className="overflow-hidden rounded-xl border border-palette-gray-divider bg-palette-gray-subtle">
          <div className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-palette-gray-secondary">
            Dates for {month ?? "the selected month"}
          </div>
          {preview ? (
            <div className="flex items-center justify-between gap-3 border-t border-palette-gray-divider bg-white px-4 py-2.5 text-sm">
              <span className="text-palette-gray-heading">
                {fmtDate(preview.statementDate)}
                <span className="mx-2 text-palette-gray-placeholder">→</span>
                {fmtDate(preview.dueDate)}
              </span>
              <span className="text-xs text-palette-gray-secondary">
                {Math.round(
                  (new Date(preview.dueDate) - new Date(preview.statementDate)) / 86400000,
                )}{" "}
                days to pay
              </span>
            </div>
          ) : (
            <div className="px-4 pb-4 text-sm text-palette-gray-placeholder">
              Enter both days to see the dates this produces.
            </div>
          )}
        </div>

        {selectedCard && (
          <div className="flex items-center gap-2 text-xs text-palette-gray-secondary">
            Paying
            <span className="font-medium text-palette-gray-heading">{selectedCard.label}</span>
            <CurrencyBadge currency={selectedCard.currency} />
          </div>
        )}
      </div>
    </Modal>
  );
}
